'use client';

import { abbreviateNumber } from '@repo/utils/abbreviate-number';
import { divide } from '@repo/utils/calc-utils';
import { cn } from '@repo/utils/cn';
import { CURRENCY_SYMBOLS, getNumericFormatProps } from '@repo/utils/get-numeric-format-props';
import { numericFormatter } from 'react-number-format';
import { useAmountText } from './context';

export type AmountTextProps = {
  currency?: string;
  className?: string;
  value: number;
  abbreviateAmount?: boolean;
};

/**
 * A reusable component for displaying an amount of money in text format based on the currency.
 */
export const AmountText = ({ currency, className, value, abbreviateAmount = false }: AmountTextProps) => {
  const { useSymbol } = useAmountText();

  return (
    <span
      suppressHydrationWarning
      className={cn(className)}
      data-testid="amount-text"
    >
      {formatAmount(value, currency, useSymbol, abbreviateAmount)}
    </span>
  );
};

AmountText.displayName = 'AmountText';

/**
 * A reusable component for displaying an amount of money in text format with a sign.
 *
 * @param value The amount of money to display
 * @param currency The currency of the amount
 * @param isPositive Whether the amount is positive
 * @param isNegative Whether the amount is negative
 * @param showSign Whether to show the sign
 * @param showOppositeSigns Whether to show opposite signs
 * @returns A formatted amount with a sign
 */
export const AmountTextWithSign = ({
  value,
  currency,
  isPositive: isPositiveOverride,
  isNegative: isNegativeOverride,
  showSign = true,
  showOppositeSigns = false,
}: {
  value: number;
  currency: string;
  isPositive?: boolean;
  isNegative?: boolean;
  showSign?: boolean;
  showOppositeSigns?: boolean;
}) => {
  const isPositive = isPositiveOverride ?? value > 0;
  const isNegative = isNegativeOverride ?? value < 0;

  return (
    <div className="flex items-center gap-x-0.5">
      {showSign && (isNegative || (showOppositeSigns && isNegative)) && (
        <span
          className={cn({
            'vt-colors-statuses-success-default': showOppositeSigns,
            'vt-colors-statuses-fail-default': !showOppositeSigns,
          })}
        >
          -
        </span>
      )}
      {showSign && (isPositive || (showOppositeSigns && isPositive)) && (
        <span
          className={cn({
            'vt-colors-statuses-success-default': !showOppositeSigns,
            'vt-colors-statuses-fail-default': showOppositeSigns,
          })}
        >
          +
        </span>
      )}
      <AmountText
        value={value}
        currency={currency}
        className={cn({
          'vt-colors-statuses-success-default': (isPositive && !showOppositeSigns) || (isNegative && showOppositeSigns),
          'vt-colors-statuses-fail-default': (isNegative && !showOppositeSigns) || (isPositive && showOppositeSigns),
        })}
      />
    </div>
  );
};

AmountTextWithSign.displayName = 'AmountTextWithSign';

/**
 * Formats a numeric value as a currency string OR returns an abbreviated version.
 *
 * @param value            The numeric value to format (in cents for USD, in standard units otherwise)
 * @param currency         The currency code (default: 'USD')
 * @param useSymbol        Whether to use the currency symbol (default: false)
 * @param abbreviateAmount If true, returns an abbreviated string ("1.2K", "3.5M", etc.) instead of full formatting
 */
export const formatAmount = (
  value: number,
  currency: string = 'USD',
  useSymbol: boolean = false,
  abbreviateAmount: boolean = false
): string => {
  const realValue = currency === 'USD' ? divide(value, 100) : value;

  if (abbreviateAmount) {
    const { prefix = '', suffix = '' } = getNumericFormatProps(currency as keyof typeof CURRENCY_SYMBOLS, {
      useSymbol,
    });
    const abbreviated = abbreviateNumber(realValue);
    return `${prefix}${abbreviated}${suffix}`;
  }

  const extraProps = getNumericFormatProps(currency as keyof typeof CURRENCY_SYMBOLS, { useSymbol });

  return numericFormatter(realValue.toString(), {
    ...extraProps,
    allowNegative: false,
  });
};

export const formatValue = (value: number, currency: string = 'USD'): number => {
  return currency === 'USD' ? divide(value, 100) : value;
};
